<template>
  <!-- eslint-disable  max-len -->
  <div :class="['main-window', hideGuide ? 'main-window__no-guide' : 'main-window__guide']">
    <div class="device-management">
      <transition name="fade">
        <simple-feedback-modal
          v-if="showFeedbackModal"
          @feedbackSent="deviceFeedbackSubmitted"
          @close="toggleFeedbackModal"
        />
      </transition>
      <transition name="fade">
        <add-devices-modal
          v-if="showAddDeviceModal"
          :loading="loading"
          :scanned-address="$attrs.newDevice"
          @add-device="createDevices"
          @close="toggleAddDeviceModal"
        />
      </transition>
      <transition name="fade">
        <network-settings-modal
          v-if="showNetworkSettings"
          :device="selectedDevice"
          :loading="loading"
          @close="toggleNetworkSettings"
          @submit-ethernet="updateEthernetSettings"
          @submit-wifi="updateWifiSettings"
          @submit-auth-method-settings="updateAuthMethodSettings"
        />
      </transition>
      <transition name="fade">
        <confirmation-modal
          v-if="showDeleteConfirmation"
          :confirmation-type="deleteConfirmation.type"
          :title="deleteConfirmation.title"
          :description="deleteConfirmation.description"
          :button-text="deleteConfirmation.button"
          @confirm="deleteDeviceGroup"
          @cancel="toggleDeleteConfirmation"
        />
      </transition>
      <transition name="fade">
        <confirmation-modal
          v-if="showFirmwareUpgradeConfirmation"
          :confirmation-type="firmwareConfirmation.type"
          :title="firmwareConfirmation.title"
          :description="firmwareConfirmation.description"
          :button-text="firmwareConfirmation.button"
          @confirm="upgradeDeviceFirmware"
          @cancel="toggleFirmwareUpdateConfirmation"
        />
      </transition>
      <transition name="fade">
        <updating-device-modal
          v-if="showUpdatingDeviceModal"
          :device="selectedDevice"
          @close="toggleUpdatingDeviceModal"
        />
      </transition>
      <transition name="fade">
        <remote-authorize-modal
          v-if="showRemoteAuthorize"
          :device="selectedDevice"
          :loading="loading"
          @close="closeRemoteAuthorize"
        />
      </transition>
      <div class="page-topbar">
        <div class="page-topbar__title">
          Device Management
        </div>
        <div v-tooltip="{ content: actionNotAvailableMessage }">
          <button
            id="add-device-button"
            :disabled="!canCreateDevice"
            class="btn btn--raised"
            @click="toggleAddDeviceModal"
          >
            Add Device
          </button>
        </div>
      </div>
      <div class="device-management-actionbar">
        <div class="searchbar-container">
          <span
            class="searchbar-icon"
            v-html="searchIcon"
          ></span>
          <input
            id="search-input"
            v-model.lazy="searchTerm"
            class="searchbar"
            placeholder="Search Devices..."
          />
          <span
            v-show="searchTerm"
            id="clear-search"
            v-tooltip="{ content: 'Clear Search', placement: 'right' }"
            class="searchbar-clear"
            @click="clearSearch"
            v-html="clearIcon"
          />
        </div>
        <pagination
          :loading="fetching"
          :page.sync="devicePaginationParams.page"
          :per-page.sync="devicePaginationParams.perPage"
          :total-rows.sync="totalDevices"
          :rows-per-page-label="'Devices per page'"
        />
      </div>
      <template v-if="deviceGroups.length > 0">
        <template v-for="(group, index) in deviceGroups">
          <div
            :key="index"
            class="device-items"
          >
            <template v-for="(device, deviceIndex) in group">
              <div
                :id="'device-item-' + deviceIndex"
                :key="device.serialNumber"
                class="device-item"
                :data-testid="device.serialNumber"
              >
                <div
                  v-tooltip="{ content: device.serialNumber }"
                  class="device__port"
                >
                  {{ device.type }}
                  {{ device.devicePort }}
                </div>
                <div class="device__status">
                  <span
                    :class="[
                      group[0].isOnline ? '' : 'device__status__indicator--red',
                      'device__status__indicator',
                    ]"
                  />
                  {{ group[0].isOnline ? "active" : "inactive" }}
                </div>
                <template v-if="device.isEditMode">
                  <div class="device__input">
                    <input
                      v-model="device.newName"
                      v-shortkey="['enter']"
                      v-focus
                      class="form-field"
                      type="text"
                      maxlength="50"
                      @shortkey="editName(device)"
                    />
                    <div class="device-actions">
                      <template v-if="loading">
                        <loader :size="25" />
                      </template>
                      <template v-else>
                        <span
                          v-tooltip="{ content: 'Save' }"
                          class="device-actions__action"
                          @click="editName(device)"
                          v-html="saveIcon"
                        />
                        <span
                          v-tooltip="{ content: 'Cancel' }"
                          v-shortkey="['esc']"
                          class="device-actions__action"
                          @click="toggleEditMode(device)"
                          @shortkey="toggleEditMode(device)"
                          v-html="cancelIcon"
                        />
                        <div class="error">
                          {{ errorMessage }}
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div
                    v-tooltip="{ content: 'Edit Door Name' }"
                    class="device__name"
                    @click="toggleEditMode(device)"
                  >
                    {{ device.name }}
                  </div>
                </template>
                <template>
                  <div>
                    <button
                      v-if="
                        device.isFirmwareUpdateRequired &&
                          isParentDevice(device) &&
                          group[0].isOnline
                      "
                      id="upgrade-device-firmware"
                      class="device__firmware-upgrade"
                      @click="toggleFirmwareUpdateConfirmation(device)"
                    >
                      Update Firmware
                    </button>
                  </div>
                </template>
                <div
                  :class="[
                    { 'device__status-label--disabled': !device.isStepUp },
                    'device__status-label',
                  ]"
                  style="display: inline-block"
                  data-testid="stepup-label"
                >
                  {{ device.isStepUp ? "enabled" : "disabled" }}
                </div>
                <toggle
                  :toggle.sync="device.isStepUp"
                  @toggle-clicked="toggleDeviceBypass(device)"
                >
                </toggle>
                <template v-if="isParentDevice(device)">
                  <div
                    id="device-menu"
                    class="device__menu-icon"
                    style="display: inline-block"
                    @click.stop="toggleMenu(device)"
                  >
                    <span v-html="menuIcon" />
                  </div>
                </template>
                <transition name="fade">
                  <template v-if="device.showMenu">
                    <ul
                      v-shortkey="['esc']"
                      v-click-outside="closeDeviceMenus"
                      class="device-dropdown"
                      @shortkey="closeDeviceMenus"
                    >
                      <li
                        id="settings-device-menu"
                        @click="toggleNetworkSettings(device)"
                      >
                        Settings
                      </li>
                      <li
                        id="remote-authorize-device-menu"
                        v-tooltip="{
                          content: device.showRemoteAuth
                            ? ''
                            : 'This feature is currently only available on Safes',
                        }"
                        :class="{ disabled: !device.showRemoteAuth }"
                        @click="openRemoteAuthorize(device)"
                      >
                        Remote Authorize Open
                      </li>
                      <li
                        id="delete-device-menu"
                        v-tooltip="{ content: actionNotAvailableMessage }"
                        :class="{ disabled: !canDeleteDevice }"
                        @click="toggleDeleteConfirmation(group)"
                      >
                        Delete Device
                      </li>
                    </ul>
                  </template>
                </transition>
              </div>
            </template>
          </div>
        </template>
      </template>
      <template v-else>
        <div class="empty-state mt-2">
          <template v-if="fetching || loading">
            Loading Devices...
          </template>
          <template v-else>
            <template v-if="!canCreateDevice">
              <div class="empty-state__description mt-1">
                <strong>No devices could be found.</strong>
                <p>
                  This may be because no devices have been added to your account or to any group you
                  have access to.
                </p>
                Please contact your Account Administrator to add devices to your group.
              </div>
            </template>
            <template v-else>
              <div class="empty-state__description mt-1">
                <strong>No devices could be found.</strong>
                <p>To add a device, scan the QR code on your Link device to begin setup.</p>
                <p>Alternatively, click Add Device and enter the device's MAC address manually.</p>
              </div>
            </template>
          </template>
        </div>
      </template>
    </div>
    <div class="help-banner">
      <div class="help-banner__item">
        <span
          class="help-banner__icon"
          v-html="searchIcon"
        />
        <a
          class="help-banner__link"
          target="_blank"
          href="https://bioconnect.com/add-a-device/"
        >
          How to add a device
        </a>
      </div>
      <div class="help-banner__item">
        <img
          :src="networkingIcon"
          class="help-banner__icon"
        />
        <a
          class="help-banner__link"
          target="_blank"
          href="https://bioconnect.com/resources/install-guides/device-networking/"
        >
          Device Networking
        </a>
      </div>
    </div>
  </div>
  <!-- eslint-enable  max-len -->
</template>

<script>
import deviceQueries from '@/graphql/devices.js'

import REGEX from '@/global/utilities.js'
import Loader from '@/components/loader.vue'
import AddDevicesModal from '@/components/addDevices.vue'
import NetworkSettingsModal from '@/components/networkSettings/deviceNetworkSettings.vue'
import Pagination from '@/components/pagination.vue'
import Toggle from '@/components/toggle.vue'
import ConfirmationModal from '@/components/confirmationModal.vue'
import SimpleFeedbackModal from '@/components/simpleFeedbackModal.vue'
import UpdatingDeviceModal from './updatingDeviceModal.vue'
import RemoteAuthorizeModal from '@/components/remoteAuthorizeModal.vue'

import feather from 'feather-icons'
import networkingIcon from '@/assets/routernetwork.svg'

export default {
  name: 'DeviceManagement',
  components: {
    Loader,
    AddDevicesModal,
    NetworkSettingsModal,
    Pagination,
    Toggle,
    ConfirmationModal,
    SimpleFeedbackModal,
    UpdatingDeviceModal,
    RemoteAuthorizeModal,
  },
  mixins: [deviceQueries],
  data() {
    return {
      fetching: false,
      loading: false,
      editMode: false,
      showFeedbackModal: false,
      showAddDeviceModal: false,
      showNetworkSettings: false,
      showDeleteConfirmation: false,
      showFirmwareUpgradeConfirmation: false,
      showUpdatingDeviceModal: false,
      showRemoteAuthorize: false,
      errorMessage: '',
      selectedDevice: {},
      selectedDeviceGroup: [],
      deviceGroups: [],
      firmwareDevice: null,
      searchTerm: '',
      totalDevices: 0,
      devicePaginationParams: {
        page: 1,
        perPage: 10,
      },
      deleteConfirmation: {
        type: 'danger',
        title: 'Delete Device?',
        description:
          'This will delete the device permanently and may impact authentication and access. Device will have to be re-added manually to get protected by BioConnect. Are you sure?',
        button: 'Delete Device',
      },
      cancelIcon: feather.icons.x.toSvg({width: '20px', height: '20px'}),
      saveIcon: feather.icons.check.toSvg({width: '20px', height: '20px'}),
      menuIcon: feather.icons['more-vertical'].toSvg(),
      searchIcon: feather.icons.search.toSvg(),
      clearIcon: feather.icons.x.toSvg({width: '1em'}),
      networkingIcon: networkingIcon,
      hideGuide: JSON.parse(localStorage.getItem('hideGuide', false)),
      canDeleteDevice: false,
      canCreateDevice: false,
    }
  },
  computed: {
    actionNotAvailableMessage() {
      return this.canDeleteDevice ? '' : 'Action not available for User Permission Level'
    },
    firmwareConfirmation() {
      return {
        type: 'friendly',
        title: 'Firmware Update Recommended',
        description: `
          Your current firmware (${this.firmwareDevice.firmware}) is out of date.<br>
          Please upgrade to the latest firmware.<br>
          <br>
          This usually takes 2-5 minutes to complete.<br>
          Your device will restart.<br>
          <br>
          Firmware Upgrades will impact accessibility while in progress, a full resynchronization of all user credentials will be required.
          Please do this while ${this.firmwareDevice.type.toLowerCase()} access is not required and the ${this.firmwareDevice.type.toLowerCase()} has a stable internet connection.
        `,
        button: 'Update Now',
      }
    },
  },
  watch: {
    searchTerm() {
      this.devicePaginationParams = {
        ...this.devicePaginationParams,
        page: 1,
      }
    },
    // Fetch devices when pagination values change
    devicePaginationParams: {
      handler: function () {
        this.fetchDevices()
      },
      deep: true,
    },
  },
  created() {
    this.canDeleteDevice = this.$can('delete', 'device')
    this.canCreateDevice = this.$can('create', 'device')
    this.fetchDevices()
    if (this.$attrs.newDevice) {
      this.toggleAddDeviceModal()
    }
    // Handle closing the welcome banner
    window.addEventListener('hideGuide', () => {
      this.hideGuide = true
    })
  },
  methods: {
    async fetchDevices() {
      this.fetching = true
      try {
        const _deviceListResult = await this.listDevicesQuery(
          this.devicePaginationParams,
          this.searchTerm,
        )
        const {deviceList, totalRows} = _deviceListResult
        this.totalDevices = totalRows
        this.deviceGroups = this.formatDevices(deviceList)
        this.fetching = false
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to fetch devices.')
        console.log('Fetch Devices Failed.', error)
      }
    },
    /*
     * The parent device must be created before the child devices.
     * To avoid any race conditions, we take the first item of the array which
     * is the parent device, it must get a successful response, before
     * proceeding to create the remaining child devices.
     */
    async createDevices(newDevices) {
      const [_parentDevice] = newDevices
      const _childDevices = newDevices.slice(1, newDevices.length)
      this.loading = true
      try {
        await this.createDeviceMutation(_parentDevice)
        await this.createChildDevices(_childDevices)
      } catch (error) {
        this.loading = false
        this.deviceErrorMessage(error, 'Unable to create parent device.')
      }
    },
    async createChildDevices(childDevices) {
      try {
        // eslint-disable-next-line no-loops/no-loops
        for (const device of childDevices) {
          await this.createDeviceMutation(device)
        }

        this.loading = false
        this.toggleAddDeviceModal()
        this.fetchDevices()
        this.displayAlert('success', 'Device Added.')
        if (this.isDeviceFeedbackNotSubmitted()) {
          this.toggleFeedbackModal()
        }
      } catch (error) {
        this.loading = false
        this.deviceErrorMessage(error, 'Unable to create child device.')
        console.log(error)
      }
    },
    async updateDevice(device) {
      this.selectedDevice = device
      device.isLoading = true
      try {
        const result = await this.updateDeviceMutation(device)
        device.isLoading = false
        this.displayAlert('success', 'Device updated.')
        return result
      } catch (error) {
        device.isLoading = false
        this.deviceErrorMessage(error, 'Unable to update device.')
        throw new Error(error)
      }
    },
    async updateDeviceName(device) {
      this.selectedDevice = device
      device.isLoading = true
      try {
        const result = await this.updateDeviceNameMutation(device)
        device.isLoading = false
        this.displayAlert('success', 'Device updated.')
        return result
      } catch (error) {
        device.isLoading = false
        this.deviceErrorMessage(error, 'Unable to update device.')
        throw new Error(error)
      }
    },
    async deleteDeviceGroup() {
      const [_firstDevice] = this.selectedDeviceGroup
      const _parentAddress = _firstDevice.parentDevice
      const _numberOfDevices = this.getNumberOfChildren(_firstDevice.type)
      const _deviceGroupAddresses = this.generateAddressGroup(_parentAddress, _numberOfDevices)
      try {
        // eslint-disable-next-line no-loops/no-loops
        for (const serialNumber of _deviceGroupAddresses) {
          const result = await this.deleteDeviceMutation(serialNumber)
          console.log(result)
        }

        this.toggleDeleteConfirmation()
        this.fetchDevices()
        this.displayAlert('success', 'Removed Device.')
      } catch (error) {
        this.displayAlert('fail', 'Unable to remove device.')
        console.log(error)
      }
    },
    async upgradeDeviceFirmware() {
      try {
        const result = await this.upgradeDeviceFirmwareMutation(this.firmwareDevice.serialNumber)
        console.log(result)
        this.firmwareDevice.isFirmwareUpdateRequired = false
        this.toggleFirmwareUpdateConfirmation()
        this.displayAlert('success', 'Firmware update request sent.')
      } catch (error) {
        this.displayAlert('fail', 'Unable to send firmware update request.')
        console.log(error)
      }
    },
    deviceErrorMessage(error, defaultMessage) {
      const _errorMessage = error.message
      let _alertMessage = 'Device Error'
      if (_errorMessage.includes('device exists')) {
        _alertMessage = 'Device already exists.'
      } else if (_errorMessage.includes('Max number')) {
        _alertMessage = 'Maximum number of two-factor enabled doors reached.'
      } else {
        _alertMessage = defaultMessage
      }
      this.displayAlert('fail', _alertMessage)
      console.log('Device Error.', error)
    },
    macAddressDifference(childDevice, parentDevice) {
      const _baseHex = 16
      const _childString = childDevice.split(':').join('')
      const _childInt = parseInt(_childString, _baseHex)
      const _parentString = parentDevice.split(':').join('')
      const _parentInt = parseInt(_parentString, _baseHex)
      return _childInt - _parentInt
    },
    /*
     * Group devices by their parent devices.
     * Create an object with keys of parent serial numbers and their value
     * being an array of the devices containing the same parent.
     * Convert to an array of arrays to easier iterate through in the view.
     */
    formatDevices(devices) {
      const _context = this
      const _devices = devices.reduce((formattedDevices, device) => {
        const {parentDevice, serialNumber} = device
        const _devicePort = _context.macAddressDifference(serialNumber, parentDevice)
        const _updatedDevice = {
          devicePort: _devicePort,
          showMenu: false,
          isEditMode: false,
          isLoading: false,
          isStepUp: !device.isBypass,
          showRemoteAuth: device.type === 'safe',
          ...device,
        }
        if (!formattedDevices.hasOwnProperty(parentDevice)) {
          formattedDevices[parentDevice] = []
        }
        formattedDevices[parentDevice].push(_updatedDevice)
        return formattedDevices
      }, {})
      return Object.keys(_devices).map((device) => {
        return _devices[device]
      })
    },
    async editName(device) {
      const _oldDeviceName = device.name
      device.name = device.newName.trim()
      if (REGEX.deviceName.test(device.name)) {
        this.loading = true
        try {
          await this.updateDeviceName(device)
          this.toggleEditMode(device)
          this.loading = false
        } catch (error) {
          this.loading = false
          device.name = _oldDeviceName
        }
      } else {
        this.errorMessage = 'Please enter a valid device name.'
        device.name = _oldDeviceName
      }
    },
    isParentDevice(device) {
      return device.serialNumber === device.parentDevice
    },
    async updateWifiSettings(device) {
      try {
        await this.updateDeviceWifiMutation(device)
        this.toggleNetworkSettings(device)
        this.displayAlert('success', 'Device wi-fi settings saved.')
        this.toggleUpdatingDeviceModal(device)
      } catch (error) {
        this.displayAlert('fail', 'Unable to update device wi-fi settings.')
        console.log(error)
      }
    },
    async updateEthernetSettings(device) {
      try {
        await this.updateDeviceEthernetMutation(device)
        this.toggleNetworkSettings(device)
        this.displayAlert('success', 'Device ethernet settings saved.')
        this.toggleUpdatingDeviceModal(device)
      } catch (error) {
        this.displayAlert('fail', 'Unable to update device ethernet settings.')
        console.log(error)
      }
    },
    async updateAuthMethodSettings(device) {
      try {
        await this.updateDeviceAuthModeMutation(device)
        this.toggleNetworkSettings(device)
        this.displayAlert('success', 'Device Authentication Mode saved.')
        this.toggleUpdatingDeviceModal(device)
      } catch (error) {
        this.displayAlert('fail', 'Unable to update device settings.')
        console.log(error)
      }
    },
    closeDeviceMenus() {
      this.deviceGroups.forEach((group) => {
        group.forEach((device) => {
          device.showMenu = false
        })
      })
    },
    async toggleDeviceBypass(device) {
      device.isBypass = device.isStepUp
      try {
        await this.updateDevice(device)
      } catch (error) {
        device.isStepUp = !device.isStepUp
      }
    },
    clearSearch() {
      this.searchTerm = ''
    },
    isDeviceFeedbackNotSubmitted() {
      return localStorage.getItem('deviceFeedbackSubmitted') === null
    },
    deviceFeedbackSubmitted() {
      localStorage.setItem('deviceFeedbackSubmitted', true)
    },
    toggleFeedbackModal() {
      this.showFeedbackModal = !this.showFeedbackModal
    },
    toggleEditMode(device) {
      device.isEditMode = !device.isEditMode
      device.newName = device.name
      this.errorMessage = ''
    },
    toggleAddDeviceModal() {
      this.showAddDeviceModal = !this.showAddDeviceModal
    },
    toggleFirmwareUpdateConfirmation(device) {
      this.showFirmwareUpgradeConfirmation = !this.showFirmwareUpgradeConfirmation
      if (this.showFirmwareUpgradeConfirmation) {
        this.firmwareDevice = device
      } else {
        this.firmwareDevice = null
      }
    },
    toggleUpdatingDeviceModal(device) {
      /*
       * When the update device modal is closed we update the device record in the device list
       * in order to prevent stale data
       */
      if (this.showUpdatingDeviceModal) {
        const deviceInd = this.deviceGroups[0].findIndex((d) => {
          return d.serialNumber === device.serialNumber
        })

        // Only update if the device is found
        if (deviceInd >= 0) {
          const [[_formattedDevice]] = this.formatDevices([device])
          this.deviceGroups[0][deviceInd] = _formattedDevice
          this.selectedDevice = _formattedDevice
        }
      }

      this.showUpdatingDeviceModal = !this.showUpdatingDeviceModal
    },
    /*
     * A mouseEvent will come in (from click-outside vue directive)
     * if a click happens outside of the menu
     * To handle closing the menu, we need to close all device menus since
     * we don't know which specific menu to close. For some unknown reason
     * the .stop directive doesn't work with inline event handling and we can't
     * pass the device object on a click-outside event.
     */
    toggleMenu(device) {
      this.closeDeviceMenus()
      device.showMenu = !device.showMenu
    },
    toggleDeleteConfirmation(group) {
      if (!this.canDeleteDevice) {
        return
      }

      this.closeDeviceMenus()
      this.showDeleteConfirmation = !this.showDeleteConfirmation
      this.selectedDeviceGroup = group
      if (!this.showDeleteConfirmation) {
        this.selectedDeviceGroup = []
      }
    },
    toggleNetworkSettings(device) {
      this.closeDeviceMenus()
      this.selectedDevice = device
      this.showNetworkSettings = !this.showNetworkSettings
    },
    openRemoteAuthorize(device) {
      // Can only create authorization code for safes
      if (device.type !== 'safe') {
        return
      }
      this.closeDeviceMenus()
      this.showRemoteAuthorize = true
      this.selectedDevice = device
    },
    closeRemoteAuthorize() {
      this.showRemoteAuthorize = false
    },
  },
}
</script>

<style lang="scss" scoped>
.device-management {
  flex: 1;
  &-actionbar {
    z-index: 10;
    background: $white;
    position: sticky;
    top: 0;
    padding: 1em 0;
  }
}
.device {
  &-item {
    display: grid;
    grid-template-columns: 100px 100px auto 130px 90px 50px 70px;
    grid-template-rows: 30px;
    justify-items: flex-start;
    align-items: center;
    position: relative;
    box-sizing: border-box;
    border-bottom: 1px solid $off-white;
    padding: 1em;
    width: calc(100% - 1em);
    margin-left: 1em;
    &:last-child {
      border: none;
    }
    @media #{$mobile} {
      grid-template-columns: 1fr 2fr 1fr 30px;
      margin: 0;
      padding: 1em 0;
      width: 100%;
    }
  }
  &-items {
    width: 100%;
    border-bottom: 1px solid $dark-grey;
  }
  &__status {
    @include flex-display();
    @include flex-align-items(center);
    text-transform: uppercase;
    font-size: $tiny-font;
    font-weight: 600;
    color: $dark-grey;
    &__indicator {
      display: inline-block;
      margin-right: 0.8em;
      height: 8px;
      width: 8px;
      border-radius: 50%;
      background-color: $green;
      box-shadow: 0 0 10px 1px $green;
      &--red {
        background-color: $red;
        box-shadow: 0 0 10px 1px $red;
      }
    }
    &-label {
      background: lighten($green, 43%);
      color: $green;
      font-weight: 600;
      text-transform: uppercase;
      font-size: $tiny-font;
      padding: 8px;
      border-radius: 5px;
      width: fit-content;
      height: fit-content;
      &--disabled {
        background: lighten($red, 38%);
        color: $red;
      }
      @media #{$mobile} {
        display: none;
      }
    }
  }
  &__name {
    font-size: $large-font;
    font-weight: 600;
    color: $dark-grey;
    &:hover {
      cursor: pointer;
    }
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
  }
  &__firmware-upgrade {
    font-family: "Work Sans", Helvetica, Arial, sans-serif !important;
    color: $yellow;
    border: none;
    background: none;
    text-decoration-line: underline;
    &:hover {
      cursor: pointer;
      color: darken($yellow, 5%);
    }
  }
  &__port {
    cursor: default;
    font-size: $small-font;
    color: $grey-text;
    background: $off-white;
    color: $grey-text;
    font-weight: 600;
    text-transform: uppercase;
    font-size: $tiny-font;
    padding: 8px;
    border-radius: 5px;
    width: fit-content;
    height: fit-content;
    @media #{$mobile} {
      display: none;
    }
  }
  &__bypass {
    @media #{$mobile} {
      display: none;
    }
  }
  &__input {
    @include flex-display();
    @include flex-align-items(center);
    width: 100%;
    @media #{$large-display} {
      width: 60%;
    }
  }
  &-actions {
    @include flex-display();
    width: 100%;
    margin-left: 1em;
    &__action {
      @include flex-display();
      @include flex-align-items(center);
      margin-right: 0.5em;
      transition: transform 0.2s ease-in-out;
      &:hover {
        cursor: pointer;
        transform: scale(1.3);
      }
    }
  }
  &__menu-icon {
    justify-self: flex-end;
    color: $dark-grey;
    width: 25px;
    height: 25px;
    padding: 0.5em;
    &:hover {
      cursor: pointer;
      background: $off-white;
      border-radius: 50%;
    }
  }
  &-dropdown {
    position: absolute;
    right: 55px;
    top: 5px;
    z-index: 999;
    padding: 0;
    background: #ffffff;
    color: #333333;
    border-radius: 5px;
    box-shadow: 0px 1px 10px 0px rgba(0, 0, 0, 0.2);
    list-style: none;
    &__divider {
      border-bottom: 1px solid $light-grey;
    }
    li {
      @include flex-display();
      @include flex-align-items(center);
      padding: 1em 1.5em;
      color: $dark-grey;
      .dropdown-icon {
        @include flex-display();
        @include flex-align-items(center);
        margin-right: 0.5em;
        width: 1em;
      }
      &:hover {
        background-color: $off-white;
        cursor: pointer;
        &:last-child {
          border-bottom-left-radius: 5px;
          border-bottom-right-radius: 5px;
        }
        &:first-child {
          border-top-left-radius: 5px;
          border-top-right-radius: 5px;
        }
      }
      &:last-child {
        border-bottom: none;
      }
      &.disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }
}
</style>
